import React, { useEffect, useRef } from 'react';
import CodeMirror from 'codemirror';
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/dracula.css';
import 'codemirror/mode/javascript/javascript';
import 'codemirror/addon/edit/closetag';
import 'codemirror/addon/edit/closebrackets';

const CLIENT_CODE_CHANGE = 'client-code-change';
const CLIENT_CODE_SYNC = 'client-code-sync';

function Editor({ channelRef, channelReady, userId, onCodeChange }) {
    const editorRef = useRef(null);
    const onCodeChangeRef = useRef(onCodeChange);
    const emitTimerRef = useRef(null);

    onCodeChangeRef.current = onCodeChange;

    useEffect(() => {
        const textarea = document.getElementById('realtimeEditor');
        editorRef.current = CodeMirror.fromTextArea(textarea, {
            mode: 'javascript',
            theme: 'dracula',
            autoCloseTags: true,
            autoCloseBrackets: true,
            lineNumbers: true,
        });

        const handleCodeChange = (instance, changes) => {
            const { origin } = changes;
            const code = instance.getValue();

            onCodeChangeRef.current(code);

            if (origin !== 'setValue' && channelRef.current) {
                clearTimeout(emitTimerRef.current);
                emitTimerRef.current = setTimeout(() => {
                    channelRef.current?.trigger(CLIENT_CODE_CHANGE, { code });
                }, 50);
            }
        };

        editorRef.current.on('change', handleCodeChange);

        return () => {
            clearTimeout(emitTimerRef.current);
            if (editorRef.current) {
                editorRef.current.off('change', handleCodeChange);
                editorRef.current.toTextArea();
                editorRef.current = null;
            }
        };
    }, [ channelRef ]);

    useEffect(() => {
        if (!channelReady) {
            return undefined;
        }

        const channel = channelRef.current;
        if (!channel) {
            return undefined;
        }

        const applyRemoteChange = ({ code }) => {
            if (code == null || !editorRef.current) {
                return;
            }
            editorRef.current.setValue(code);
        };

        const applySync = (payload) => {
            if (payload?.targetUserId !== userId) {
                return;
            }
            if (payload?.code == null || !editorRef.current) {
                return;
            }
            editorRef.current.setValue(payload.code);
        };

        channel.bind(CLIENT_CODE_CHANGE, applyRemoteChange);
        channel.bind(CLIENT_CODE_SYNC, applySync);

        return () => {
            channel.unbind(CLIENT_CODE_CHANGE, applyRemoteChange);
            channel.unbind(CLIENT_CODE_SYNC, applySync);
        };
    }, [ channelReady, channelRef, userId ]);

    return <textarea id="realtimeEditor"></textarea>;
}

export default Editor;
